/**
 * Discord Bot API Service
 * Handles all communication with the Discord bot REST API
 */

// API Configuration
const API_BASE_URL = process.env.REACT_APP_DISCORD_API_URL || 'http://82.197.92.33:3001';
const API_KEY = process.env.REACT_APP_DISCORD_API_KEY;

// TypeScript Interfaces
export interface DiscordMember {
  id: string;
  username: string;
  displayName: string;
  discriminator: string;
  avatar: string;
  status: 'online' | 'idle' | 'dnd' | 'offline';
  bot: boolean;
  activities: Activity[];
  joinedAt: string;
  roles: string[];
}

export interface Activity {
  name: string;
  type: number; // 0: Playing, 1: Streaming, 2: Listening, 3: Watching, 4: Custom, 5: Competing
  details?: string;
  state?: string;
}

export interface ServerStats {
  totalMembers: number;
  onlineMembers: number;
  offlineMembers: number;
  boosts: number;
  boostLevel: number;
  lastUpdated: string;
}

export interface VoiceChannel {
  id: string;
  name: string;
  memberCount: number;
  members: DiscordMember[];
}

export interface Birthday {
  name: string;
  month: number;
  day: number;
  daysUntil: number;
  nextDate: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// Helper function to make API requests
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add API key if configured
    if (API_KEY) {
      headers['x-api-key'] = API_KEY;
    }

    // Merge with any additional headers from options
    if (options.headers) {
      Object.assign(headers, options.headers);
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error(`Discord API Error (${endpoint}):`, error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

// API Service Functions

/**
 * Get bot health status
 */
export async function getHealth() {
  return apiRequest<{
    status: string;
    timestamp: string;
    uptime: number;
    cache: {
      members: number;
      voiceStates: number;
    };
  }>('/api/health');
}

/**
 * Get all server members
 */
export async function getAllMembers() {
  const response = await apiRequest<{
    success: boolean;
    count: number;
    members: DiscordMember[];
  }>('/api/members');

  return {
    success: response.success,
    data: response.data?.members,
    error: response.error,
  };
}

/**
 * Get only online members (online, idle, dnd)
 */
export async function getOnlineMembers() {
  const response = await apiRequest<{
    success: boolean;
    count: number;
    members: DiscordMember[];
  }>('/api/members/online');

  return {
    success: response.success,
    data: response.data?.members,
    error: response.error,
  };
}

/**
 * Get server statistics
 */
export async function getServerStats() {
  const response = await apiRequest<{
    success: boolean;
    server: ServerStats;
  }>('/api/stats');

  return {
    success: response.success,
    data: response.data?.server,
    error: response.error,
  };
}

/**
 * Get active voice channels with members
 */
export async function getVoiceChannels() {
  const response = await apiRequest<{
    success: boolean;
    count: number;
    channels: Array<{
      id: string;
      name: string;
      members: string[];
      memberDetails: DiscordMember[];
    }>;
  }>('/api/voice');

  // Transform API response to match VoiceChannel interface
  const transformedChannels = response.data?.channels.map(channel => ({
    id: channel.id,
    name: channel.name,
    memberCount: channel.memberDetails.length,
    members: channel.memberDetails, // Use memberDetails instead of member IDs
  }));

  return {
    success: response.success,
    data: transformedChannels,
    error: response.error,
  };
}

/**
 * Get all birthdays sorted by next occurrence
 */
export async function getAllBirthdays() {
  const response = await apiRequest<{
    success: boolean;
    count: number;
    birthdays: Birthday[];
  }>('/api/birthdays');

  return {
    success: response.success,
    data: response.data?.birthdays,
    error: response.error,
  };
}

/**
 * Get upcoming birthdays (next N days)
 */
export async function getUpcomingBirthdays(days: number = 7) {
  const response = await apiRequest<{
    success: boolean;
    days: number;
    count: number;
    birthdays: Birthday[];
  }>(`/api/birthdays/upcoming?days=${days}`);

  return {
    success: response.success,
    data: response.data?.birthdays,
    error: response.error,
  };
}

/**
 * Get today's birthdays
 */
export async function getTodaysBirthdays() {
  const response = await apiRequest<{
    success: boolean;
    count: number;
    birthdays: Birthday[];
  }>('/api/birthdays/today');

  return {
    success: response.success,
    data: response.data?.birthdays,
    error: response.error,
  };
}

/**
 * Manually trigger birthday check (sends notifications)
 */
export async function triggerBirthdayCheck() {
  return apiRequest<{
    success: boolean;
    message: string;
    birthdaysToday: Birthday[];
  }>('/api/birthdays/check', {
    method: 'POST',
  });
}

// Utility Functions

/**
 * Get activity type label
 */
export function getActivityTypeLabel(type: number): string {
  const types: { [key: number]: string } = {
    0: 'Playing',
    1: 'Streaming',
    2: 'Listening to',
    3: 'Watching',
    4: '', // Custom status
    5: 'Competing in',
  };
  return types[type] || '';
}

/**
 * Get status color class
 */
export function getStatusColor(status: string): string {
  const colors: { [key: string]: string } = {
    online: 'bg-green-500',
    idle: 'bg-yellow-500',
    dnd: 'bg-red-500',
    offline: 'bg-gray-500',
  };
  return colors[status] || 'bg-gray-500';
}

/**
 * Get Discord avatar URL
 */
export function getAvatarUrl(member: DiscordMember): string {
  // API already returns full avatar URL
  if (member.avatar && member.avatar.startsWith('http')) {
    return member.avatar;
  }
  // Fallback: construct URL from hash if needed
  if (member.avatar) {
    return `https://cdn.discordapp.com/avatars/${member.id}/${member.avatar}.png?size=128`;
  }
  // Default Discord avatar (based on discriminator)
  const defaultAvatar = parseInt(member.discriminator) % 5;
  return `https://cdn.discordapp.com/embed/avatars/${defaultAvatar}.png`;
}

/**
 * Format activity display text
 */
export function formatActivity(activity: Activity): string {
  const typeLabel = getActivityTypeLabel(activity.type);
  if (activity.type === 4) {
    // Custom status
    return activity.state || activity.name;
  }
  return `${typeLabel} ${activity.name}`;
}
